import {openDialog} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

// eslint-disable-next-line no-restricted-imports -- Intended
import {isNil, path} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {GetDeliveryNoteResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  logError,
  WAREHOUSE_GRID_CODES,
  useTenant,
} from '@dms/shared';
import {
  OverviewTotalPrice,
  PURCHASE_DATE_ROW_COLUMN,
  SET_PURCHASE_DATE_ACTION_KEY,
  SetPurchaseDateDialog,
} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {DeliveryNoteItemEdit, DeliveryNoteItemEditProps} from './DeliveryNoteItemEdit';

interface ListOfItemsTabProps extends RequiredTestIdProps {
  deliveryNoteId: string;
  deliveryNote: GetDeliveryNoteResponse | Nullish;
}

const PREDEFINED_COLUMNS = [PURCHASE_DATE_ROW_COLUMN] as const;

const EDIT_ITEM_ACTION_KEY = 'editItemDN';

const PENDING_DELIVERY_NOTE_STATE = 'PENDING';

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();

  const dispatch = useDispatch();

  const [editDialogProps, setEditDialogProps] = useState<DeliveryNoteItemEditProps | null>(null);

  /**
   * Items are editable only while the delivery note is pending. The row action is gated on the
   * backend as well — this keeps the form read-only if the state changed in the background.
   */
  const isNotPendingDeliveryNote = props.deliveryNote?.state !== PENDING_DELIVERY_NOTE_STATE;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      deliveryNoteId: props.deliveryNoteId,
    }),
    [props.deliveryNoteId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    rowData,
    queryId,
    refreshData,
    deselectAll,
  }) => {
    // Bulk actions hand over every selected row id, row actions a single one.
    const deliveryNoteItemIds = isArray(rowId) ? rowId : [rowId];

    match(actionKey)
      .with(EDIT_ITEM_ACTION_KEY, () => {
        const deliveryNoteItemId = isArray(rowId) ? rowId[0] : rowId;

        if (isNil(deliveryNoteItemId)) {
          return logError('DeliveryNoteItemId is missing');
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
          // Invalidating manually onClose to keep dialog rowControl saving feature intact.
          dispatch(metadaApi.util.invalidateTags(['deliveryNote', 'headerDynamicActions']));
        };

        setEditDialogProps({
          deliveryNoteId: props.deliveryNoteId,
          deliveryNoteItemId,
          dataQueryId: queryId,
          isReadOnly: isNotPendingDeliveryNote,
          onClose: handleClose,
          'data-testid': suffixTestId('editItem', props),
        });
      })
      .with(SET_PURCHASE_DATE_ACTION_KEY, () => {
        // A bulk selection yields no single value here, so the dialog opens in "clear" mode.
        const initialPurchaseDate = path([PURCHASE_DATE_ROW_COLUMN, 'value'], rowData) as
          | string
          | undefined;

        openDialog(
          <SetPurchaseDateDialog
            headerId={props.deliveryNoteId}
            itemIds={deliveryNoteItemIds}
            headerType="DELIVERY_NOTE"
            initialPurchaseDate={initialPurchaseDate}
            onSaved={() => {
              deselectAll();
              refreshData();
              dispatch(metadaApi.util.invalidateTags(['deliveryNote', 'headerDynamicActions']));
            }}
            data-testid={suffixTestId('setPurchaseDate', props)}
          />,
          {
            id: 'SET_PURCHASE_DATE_DIALOG',
            title: i18n.t('entity.warehouse.actions.setPurchaseDate'),
            size: 'small',
            'data-testid': suffixTestId('dialogs.setPurchaseDate', props),
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const totalPriceWithoutVat = formatCurrency(
    props.deliveryNote?.totalPrice?.withoutVat?.amount ?? 0,
    props.deliveryNote?.totalPrice?.withoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    props.deliveryNote?.totalPrice?.withVat?.amount ?? 0,
    props.deliveryNote?.totalPrice?.withVat?.currency ?? tenant.tenantCurrency,
    2
  );

  return (
    <Box height="100%">
      <Show when={editDialogProps}>
        {(editDialogProps) => <DeliveryNoteItemEdit {...editDialogProps} />}
      </Show>
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.deliveryNoteItems}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          queryModifier={queryModifier}
          data-testid={suffixTestId('deliveryNoteItems', props)}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceLabel={i18n.t('entity.warehouse.labels.totalPrice')}
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </Box>
  );
}
